import React, { useState } from "react";
import { ChevronLeft, ChevronRight, Star } from "lucide-react";
import { Card, CardContent } from "~/components/ui/card";
import { Button } from "~/components/ui/button";
import { safeBuilderHtml, safeBuilderText } from "~/lib/builder-utils";

export interface Testimonial {
  author: unknown;
  title?: unknown;
  role?: unknown;
  content: unknown;
  setHTML?: boolean;
}

export const Testimonials = ({
  testimonials,
}: {
  testimonials: Testimonial[];
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  const nextTestimonial = () => {
    setCurrentIndex((prev) => (prev + 1) % testimonials.length);
  };

  const prevTestimonial = () => {
    setCurrentIndex(
      (prev) => (prev - 1 + testimonials.length) % testimonials.length,
    );
  };

  if (0 === testimonials.length) {
    return null;
  }

  return (
    <Card className="w-full overflow-hidden">
      <CardContent>
        <div className="relative grid w-full grid-cols-1 items-stretch justify-center px-8 md:px-16 lg:px-24">
          {testimonials.map((testimonial, index) => (
            <Card
              key={index}
              className={`col-start-1 row-start-1 w-full bg-white transition-all duration-300 ease-in-out ${
                index === currentIndex
                  ? "z-20 translate-x-0 rotate-0"
                  : index === (currentIndex + 1) % testimonials.length
                    ? "z-10 translate-x-[60%] rotate-6"
                    : "z-0 -translate-x-[60%] -rotate-6"
              }`}
            >
              <CardContent
                className={`flex h-full items-start gap-4 p-6 ${
                  index === currentIndex
                    ? "opacity-100"
                    : index === (currentIndex + 1) % testimonials.length
                      ? "opacity-70"
                      : "opacity-50"
                }`}
              >
                {/* {testimonial.image && (
                <img
                  src={testimonial.image}
                  alt={testimonial.name}
                  className="w-16 h-16 rounded-full object-cover"
                  width={64}
                  height={64}
                />
              )} */}
                <div className="flex flex-col items-stretch gap-2">
                  <div className="flex flex-col">
                    <h3 className="font-heading text-lg font-medium leading-none">
                      {safeBuilderText(testimonial.author)}
                    </h3>
                    {safeBuilderText(testimonial.role) && (
                      <p className="text-text-tertiary font-italic text-sm">
                        {safeBuilderText(testimonial.role)}
                      </p>
                    )}
                  </div>
                  {safeBuilderText(testimonial.title) && (
                    <p className="text-text-secondary text-xl">
                      {safeBuilderText(testimonial.title)}
                    </p>
                  )}
                  <div className="flex items-center">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <Star
                        key={i}
                        className="h-4 w-4 fill-yellow-400 text-yellow-400"
                      />
                    ))}
                    {/* <span className="ml-2 text-sm text-muted-foreground">
                    {testimonial.rating.toFixed(1)}
                  </span> */}
                  </div>
                  {testimonial.setHTML ? (
                    <p
                      dangerouslySetInnerHTML={{
                        __html: safeBuilderHtml(testimonial.content),
                      }}
                      className="text-text-secondary prose mt-2 text-sm"
                    />
                  ) : (
                    <p className="text-text-secondary mt-2 text-sm">
                      {safeBuilderText(testimonial.content)}
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
        <div className="mt-8 flex items-center justify-center gap-2">
          <Button variant="outline" size="icon" onClick={prevTestimonial}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          {testimonials.map((_, index) => (
            <Button
              key={index}
              variant="outline"
              size="icon"
              className={`h-2 w-2 rounded-full p-0 ${index === currentIndex ? "bg-primary" : ""}`}
              onClick={() => setCurrentIndex(index)}
            />
          ))}
          <Button variant="outline" size="icon" onClick={nextTestimonial}>
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};
